import { useState, useEffect } from 'react';
import { db } from '../Firebase/firebase';
import { ref, get, update, onValue } from 'firebase/database';
import { getAuth, signOut } from 'firebase/auth';
import './Admin.css';

const DRAW_DURATION = 10000; // 10 seconds

const Admin = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isDrawActive, setIsDrawActive] = useState(false);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [winner, setWinner] = useState<{ id: string; name: string } | null>(null);
  const auth = getAuth();

  // Check if draw is active on component mount
  useEffect(() => {
    const checkDrawStatus = async () => {
      try {
        const lotteryRef = ref(db, 'lottery');
        const snapshot = await get(lotteryRef);
        const data = snapshot.val();
        
        if (data?.isDrawing) {
          setIsDrawActive(true);
          const startTime = new Date(data.drawStartedAt).getTime();
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, DRAW_DURATION - elapsed);
          setTimeLeft(remaining);
          
          if (remaining > 0) {
            const timer = setInterval(() => {
              setTimeLeft(prev => {
                if (prev <= 1000) {
                  clearInterval(timer);
                  stopDraw();
                  return 0;
                }
                return Math.max(0, prev - 1000);
              });
            }, 1000);
            setTimerId(timer);
          } else {
            stopDraw();
          }
        } else {
          // Reset state when draw is not active
          setIsDrawActive(false);
          setTimeLeft(0);
          setSuccess(false);
          if (timerId) {
            clearInterval(timerId);
            setTimerId(null);
          }
          // Set winner if available
          if (data?.winner) {
            setWinner(data.winner);
          }
        }
      } catch (err) {
        console.error('Error checking draw status:', err);
      }
    };

    checkDrawStatus();

    // Set up real-time listener for lottery status changes
    const lotteryRef = ref(db, 'lottery');
    const unsubscribe = onValue(lotteryRef, (snapshot) => {
      const data = snapshot.val();
      if (!data?.isDrawing) {
        setIsDrawActive(false);
        setTimeLeft(0);
        setSuccess(false);
        if (timerId) {
          clearInterval(timerId);
          setTimerId(null);
        }
        // Set winner if available
        if (data?.winner) {
          setWinner(data.winner);
        }
      }
    });

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
      unsubscribe();
    };
  }, []);

  const stopDraw = async () => {
    try {
      // Update lottery status
      const lotteryRef = ref(db, 'lottery');
      await update(lotteryRef, {
        isDrawing: false,
        drawStartedAt: null
      });

      // Update car state
      const carStateRef = ref(db, 'carState');
      await update(carStateRef, {
        visible: false,
        showEffects: false
      });

      setIsDrawActive(false);
      setTimeLeft(0);
      if (timerId) {
        clearInterval(timerId);
        setTimerId(null);
      }
    } catch (err) {
      console.error('Error stopping draw:', err);
    }
  };

  const resetDraw = async () => {
    try {
      // Reset lottery status
      const lotteryRef = ref(db, 'lottery');
      await update(lotteryRef, {
        isDrawing: false,
        drawStartedAt: null,
        winner: null,
        countdownDate: "2026-04-19T12:20:00.000Z"
      });

      // Reset car state
      const carStateRef = ref(db, 'carState');
      await update(carStateRef, {
        visible: false,
        showEffects: false
      });

      setIsDrawActive(false);
      setTimeLeft(0);
      setSuccess(false);
      setError('');
      setShowResetConfirm(false);
      setWinner(null);
      if (timerId) {
        clearInterval(timerId);
        setTimerId(null);
      }
    } catch (err) {
      console.error('Error resetting draw:', err);
      setError('Failed to reset the draw. Please try again.');
    }
  };

  const startDraw = async () => {
    setError('');
    setSuccess(false);

    try {
      // Get participants and select winner
      const participantsRef = ref(db, 'participants');
      const participantsSnapshot = await get(participantsRef);
      const participants = participantsSnapshot.val()?.list || {};
      const participantIds = Object.keys(participants);
      
      if (participantIds.length === 0) {
        setError('No participants available for the draw');
        return;
      }

      const randomIndex = Math.floor(Math.random() * participantIds.length);
      const winnerId = participantIds[randomIndex];
      const selectedWinner = {
        id: winnerId,
        name: participants[winnerId]
      };

      // Update lottery status
      const lotteryRef = ref(db, 'lottery');
      await update(lotteryRef, {
        isDrawing: true,
        drawStartedAt: new Date().toISOString(),
        winner: null
      });

      // Update car state - initially only visible
      const carStateRef = ref(db, 'carState');
      await update(carStateRef, {
        visible: true,
        showEffects: false
      });

      // After 2 seconds (when car arrives), start effects
      setTimeout(async () => {
        console.log('⏰ 2 seconds passed - Starting effects');
        await update(carStateRef, {
          visible: true,
          showEffects: true
        });
        console.log('✅ Effects started');
      }, 2000);

      // After 9.9 seconds, stop effects
      setTimeout(async () => {
        console.log('⏰ 9.9 seconds passed - Stopping effects');
        await update(carStateRef, {
          visible: false,
          showEffects: false
        });
        await update(lotteryRef, {
          isDrawing: false,
          drawStartedAt: null,
          winner: selectedWinner
        });
         
        console.log('✅ Effects stopped');
      }, 10001);

      setSuccess(true);
      setIsDrawActive(true);
      setTimeLeft(DRAW_DURATION);

      // Set timer to stop the draw
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1000) {
            clearInterval(timer);
            stopDraw();
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
      setTimerId(timer);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to start the draw. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const formatTime = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    return `${seconds}s`;
  };

  return (
    <div className="admin-container">
      <div className="admin-form">
        <h1>Admin Panel</h1>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
        {error && <div className="error">{error}</div>}
        {success && <div className="success">Draw started successfully!</div>}
        {winner && (
          <div className="winner-display">
            <h2>Winner</h2>
            <p>{winner.name}</p>
          </div>
        )}
        {!isDrawActive && !showResetConfirm && (
          <button onClick={startDraw} className="start-draw-button">Start Draw</button>
        )}
        {isDrawActive && (
          <div className="draw-status">
            <p>Time remaining: {formatTime(timeLeft)}</p>
            <button onClick={stopDraw}>Stop Draw</button>
          </div>
        )}
        {!isDrawActive && winner && !showResetConfirm && (
          <button 
            className="reset-button"
            onClick={() => setShowResetConfirm(true)}
          >
            Reset Draw
          </button>
        )}
        {showResetConfirm && (
          <div className="confirm-dialog">
            <p>Are you sure you want to reset the draw?</p>
            <button onClick={resetDraw}>Yes, Reset</button>
            <button onClick={() => setShowResetConfirm(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin; 